/**
 * Premium Shipping Protection Auto-Add System
 * Automatically manages shipping protection based on cart value
 */

class ShippingProtection {
  constructor() {
    this.productId = 8484643209304;
    this.protectionEnabled = this.getProtectionState();
    this.isProcessing = false;
    this.lastRequestTime = 0;
    this.REQUEST_COOLDOWN = 2000; // Reduced from 5000ms to 2000ms
    this.hasFixedQuantity = false; // Flag to prevent infinite loops
    this.hasAutoAdded = false; // Flag to prevent auto-add after manual toggle
    this.lastProtectionTier = this.getLastProtectionTier(); // Track last tier to detect changes
    this.cartCache = null; // Cache cart data to avoid redundant API calls
    this.cacheTimeout = 2000; // Cache valid for 2 seconds
    this.variantIds = [
      43238395150424, 43238395183192, 43238395215960, 43238395248728, 43238395281496,
      43238395314264, 43238395347032, 43238395379800, 43238395412568, 43238395445336,
      43238395478104, 43238395510872, 43238395543640, 43238395576408, 43238395609176,
      43238395641944, 43238395674712, 43238395707480, 43238395740248, 43238395773016,
      43238395805784, 43238395838552, 43238395871320, 43238395904088, 43238395936856,
      43238395969624, 43238396002392, 43238396035160, 43238396067928, 43238396100696,
      43238396133464, 43238396166232, 43238396199000, 43238396231768, 43238396264536,
      43238396297304, 43238396330072, 43238396362840, 43238396395608, 43238396428376,
      43238396461144, 43238396493912, 43238396526680, 43238396559448, 43238396592216,
      43238396624984, 43238396657752, 43238396690520, 43238396723288, 43238396756056,
      43238396788824, 43238396821592, 43238396854360, 43238396887128, 43238396919896,
      43238396952664, 43238396985432, 43238397018200, 43238397050968, 43238397083736,
      43238397116504, 43238397149272, 43238397182040, 43238397214808, 43238397247576,
      43238397280344, 43238397313112, 43238397345880, 43238397378648, 43238397411416,
      43238397444184, 43238397476952, 43238397509720, 43238397542488, 43238397575256,
      43238397608024, 43238397640792, 43238397673560, 43238397706328, 43238397739096,
      43238397771864, 43238397804632, 43238397837400, 43238397870168, 43238397902936,
      43238397935704, 43238397968472, 43238398001240, 43238398034008, 43238398066776,
      43238398099544, 43238398132312, 43238398165080, 43238398197848, 43238398230616,
      43238398263384, 43238398296152, 43238398328920, 43238398361688, 43238398787672
    ];
    
    this.init();
  }

  init() {
    // Only operate on cart page
    if (window.location.pathname !== '/cart') {
      return;
    }

    // Listen for cart updates
    document.addEventListener('DOMContentLoaded', () => {
      this.showProtectionSection();
      this.setupCheckboxHandler();
      this.updateProtectionDisplay();
      // Auto-add protection on page load if enabled - do this immediately
      this.autoAddProtectionOnLoad();
    });
  }

  async getCartData(useCache = true) {
    // Use cached data if available and recent
    if (useCache && this.cartCache && (Date.now() - this.cartCache.timestamp) < this.cacheTimeout) {
      return this.cartCache.data;
    }

    // Fetch fresh cart data
    const response = await fetch('/cart.js');
    const cart = await response.json();
    
    // Cache the result
    this.cartCache = {
      data: cart,
      timestamp: Date.now()
    };

    return cart;
  }

  async getCartSubtotal(useCache = true) {
    // Get cart subtotal excluding shipping protection
    const cart = await this.getCartData(useCache);
    let subtotal = 0;
    cart.items.forEach(item => {
      if (item.product_id !== this.productId) {
        subtotal += item.line_price;
      }
    });
    return subtotal;
  }

  async getCurrentProtectionItem(useCache = true) {
    const cart = await this.getCartData(useCache);
    
    const protectionItems = cart.items.filter(item => item.product_id === this.productId);
    
    if (protectionItems.length > 1) {
      this.removeDuplicateProtection(protectionItems);
      return protectionItems[0]; // Return the first one
    }
    
    const protectionItem = protectionItems[0] || null;
    return protectionItem;
  }

  calculateRequiredTier(subtotal) {
    // Each tier covers $1000, starting from $0
    // Tier 1: $0-1000 = $30, Tier 2: $1001-2000 = $60, etc.
    const tier = Math.min(Math.ceil(subtotal / 100000), 100); // Convert cents to dollars and divide by 1000
    return tier;
  }

  getVariantIdForTier(tier) {
    // Tiers are 1-indexed, array is 0-indexed
    return this.variantIds[tier - 1];
  }

  getProtectionState() {
    // Default to enabled (true) unless explicitly disabled
    return localStorage.getItem('shipping_protection_enabled') !== 'false';
  }

  setProtectionState(enabled) {
    this.protectionEnabled = enabled;
    localStorage.setItem('shipping_protection_enabled', enabled.toString());
  }

  getLastProtectionTier() {
    return parseInt(localStorage.getItem('last_protection_tier')) || 0;
  }

  setLastProtectionTier(tier) {
    this.lastProtectionTier = tier;
    localStorage.setItem('last_protection_tier', tier.toString());
  }

  async updateProtection() {
    // Rate limiting check
    const now = Date.now();
    if (this.isProcessing || (now - this.lastRequestTime) < this.REQUEST_COOLDOWN) {
      return;
    }

    this.isProcessing = true;
    this.lastRequestTime = now;

    try {
      if (!this.protectionEnabled) {
        await this.removeProtection();
        return;
      }

      const subtotal = await this.getCartSubtotal();
      const currentProtection = await this.getCurrentProtectionItem();
      const requiredTier = this.calculateRequiredTier(subtotal);
      
      if (subtotal === 0) {
        // Empty cart, remove protection
        if (currentProtection) {
          await this.removeProtection();
        }
        return;
      }

      const requiredVariantId = this.getVariantIdForTier(requiredTier);

      if (!currentProtection) {
        // No protection in cart, add it
        await this.addProtection(requiredVariantId);
      } else if (currentProtection.variant_id !== requiredVariantId) {
        // Wrong tier, update it
        await this.updateProtectionTier(currentProtection, requiredVariantId);
      }
    } finally {
      // Reset processing flag after cooldown period
      setTimeout(() => {
        this.isProcessing = false;
      }, this.REQUEST_COOLDOWN / 2); // Reset flag sooner than full cooldown
    }
  }

  async addProtection(variantId, shouldReload = false) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      });
      
      if (response.status === 429) {
        setTimeout(() => this.addProtection(variantId, shouldReload), 3000);
        return;
      }
      
      if (response.ok) {
        // Invalidate cache since cart changed
        this.cartCache = null;
        
        if (shouldReload) {
          setTimeout(() => window.location.reload(), 300);
        } else {
          // Update displays immediately with reduced delay
          setTimeout(() => this.updateProtectionDisplay(), 200);
        }
      }
    } catch (error) {
      // Silent error handling
    }
  }

  async removeProtection() {
    const currentProtection = await this.getCurrentProtectionItem();
    if (currentProtection) {
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: currentProtection.key,
            quantity: 0
          })
        });
        
        if (response.ok) {
          // Invalidate cache since cart changed
          this.cartCache = null;
          return true;
        } else {
          return false;
        }
      } catch (error) {
        return false;
      }
    } else {
      return true;
    }
  }

  async updateProtectionTier(currentProtection, newVariantId, shouldReload = false) {
    // Remove current protection and add new tier
    await this.removeProtection();
    await this.addProtection(newVariantId, shouldReload);
  }

  setupCheckboxHandler() {
    const checkbox = document.getElementById('shipping-protection-toggle');
    if (checkbox) {
      checkbox.checked = this.protectionEnabled;
      checkbox.addEventListener('change', (e) => {
        this.setProtectionState(e.target.checked);
        
        if (e.target.checked) {
          this.manualUpdateProtection().then(() => {
            setTimeout(() => window.location.reload(), 300);
          });
        } else {
          this.removeProtection().then((success) => {
            if (success) {
              setTimeout(() => window.location.reload(), 300);
            } else {
              e.target.checked = true;
              this.setProtectionState(true);
            }
          });
        }
      });
    }
  }

  observeCartChanges() {
    // Listen for cart form submissions
    const cartForm = document.querySelector('form[action="/cart"]');
    if (cartForm) {
      cartForm.addEventListener('submit', () => {
        // Delay to allow cart update to process
        setTimeout(() => this.updateProtection(), 300);
      });
    }
  }



  async removeDuplicateProtection(protectionItems) {
    for (let i = 1; i < protectionItems.length; i++) {
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: protectionItems[i].key,
            quantity: 0
          })
        });
      } catch (error) {
        // Silent error handling
      }
    }
  }

  async fixProtectionQuantity(protectionItem) {
    // Fix protection quantity to always be 1
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: protectionItem.key,
          quantity: 1
        })
      });
      
      if (response.ok) {
        console.log('Protection quantity fixed successfully');
        // Refresh to show the updated quantity
        setTimeout(() => window.location.reload(), 500);
      } else {
        console.error('Failed to fix protection quantity:', response.status);
      }
    } catch (error) {
      console.error('Error fixing protection quantity:', error);
    }
  }

  showProtectionSection() {
    const section = document.getElementById('shipping-protection-section');
    if (section) {
      section.style.display = 'block';
    }
  }


  async updateProtectionDisplay() {
  const subtotal = await this.getCartSubtotal(); // in cents
  const tier = this.calculateRequiredTier(subtotal);

  const coveredValueDollars = tier * 1000; // <-- key change: use tier coverage blocks

  let price;
  if (tier <= 10) {
    price = coveredValueDollars * 0.03; // 3% of covered value
  } else if (tier <= 15) {
    price = 300; // flat
  } else {
    price = coveredValueDollars * 0.02; // 2% of covered value
  }

  // Optional: round to whole dollars for display
  const displayPrice = Math.round(price);

  const priceDisplay = document.getElementById('protection-price-display');
  if (priceDisplay) {
    priceDisplay.textContent = `Cost: $${displayPrice}.00`;
  }

  await this.updateFallbackProtectionDisplay();
}


  async updateFallbackProtectionDisplay() {
    const subtotal = await this.getCartSubtotal();
    const currentProtection = await this.getCurrentProtectionItem();
    const fallbackElement = document.getElementById('protection-price-fallback');
    const priceAmountElement = document.getElementById('protection-price-amount');
    
    if (fallbackElement && priceAmountElement) {
      if (this.protectionEnabled && currentProtection && currentProtection.line_price > 0 && subtotal > 0) {
        // Show fallback with current protection price
        const priceFormatted = this.formatMoney(currentProtection.line_price);
        priceAmountElement.textContent = priceFormatted + ' CAD';
        fallbackElement.style.display = 'flex';
      } else {
        // Hide fallback
        fallbackElement.style.display = 'none';
      }
    }
  }

  formatMoney(cents) {
    const dollars = cents / 100;
    return `$${dollars.toFixed(2)}`;
  }

  async autoAddProtectionOnLoad() {
    if (!this.protectionEnabled) {
      this.setLastProtectionTier(0);
      return;
    }

    const subtotal = await this.getCartSubtotal();
    const currentProtection = await this.getCurrentProtectionItem();
    const requiredTier = this.calculateRequiredTier(subtotal);
    
    if (subtotal === 0) {
      this.setLastProtectionTier(0);
      return;
    }

    const tierChanged = this.lastProtectionTier !== requiredTier;

    if (currentProtection) {
      const requiredVariantId = this.getVariantIdForTier(requiredTier);
      
      if (currentProtection.quantity !== 1 && !this.hasFixedQuantity) {
        this.hasFixedQuantity = true;
        await this.fixProtectionQuantity(currentProtection);
        return;
      }
      
      if (currentProtection.variant_id === requiredVariantId) {
        this.setLastProtectionTier(requiredTier);
        return;
      } else {
        this.setLastProtectionTier(requiredTier);
        await this.updateProtectionTier(currentProtection, requiredVariantId, true);
        return;
      }
    }

    const requiredVariantId = this.getVariantIdForTier(requiredTier);
    const shouldReload = tierChanged && this.lastProtectionTier !== 0;
    this.setLastProtectionTier(requiredTier);
    this.hasAutoAdded = true;
    await this.addProtection(requiredVariantId, shouldReload);
  }

  async manualUpdateProtection() {
    try {
      const subtotal = await this.getCartSubtotal();
      const currentProtection = await this.getCurrentProtectionItem();
      const requiredTier = this.calculateRequiredTier(subtotal);
      
      if (subtotal === 0) return;

      const requiredVariantId = this.getVariantIdForTier(requiredTier);

      if (!currentProtection) {
        await this.addProtection(requiredVariantId);
      } else if (currentProtection.variant_id !== requiredVariantId) {
        await this.updateProtectionTier(currentProtection, requiredVariantId);
      }
    } catch (error) {
      // Silent error handling
    }
  }
}

// Initialize shipping protection
window.shippingProtection = new ShippingProtection();